import posthog from 'posthog-js';
import { isPosthogEnabled } from '@src/utils/env';

const SPACES_PREFIX = 'https://mymathscloud.ams3.digitaloceanspaces.com/';

export type DocumentClickOutcome =
  | 'opened'
  | 'detail'
  | 'subscribe'
  | 'cart'
  | 'profile'
  | 'paywall';

type DocumentLike = Record<string, any> | null | undefined;

type TrackDocumentClickExtras = {
  source: string;
  outcome: DocumentClickOutcome;
  isSubscribed?: boolean;
  destinationUrl?: string;
};

function firstDefined<T>(...values: T[]): T | undefined {
  return values.find((value) => value !== undefined && value !== null && value !== '');
}

function asNames(value: unknown): string | undefined {
  if (!value) return undefined;
  if (typeof value === 'string') return value;
  if (Array.isArray(value)) {
    const names = value
      .map((item) => (typeof item === 'string' ? item : item?.name || item?.slug))
      .filter(Boolean);
    return names.length ? names.join(', ') : undefined;
  }
  if (typeof value === 'object') {
    return (value as { name?: string; slug?: string; title?: string }).name
      || (value as { slug?: string }).slug
      || (value as { title?: string }).title;
  }
  return undefined;
}

function getAccess(doc: Record<string, any>): string {
  if (doc.isSubscription || doc.requires_subscription) return 'subscription';
  if (doc.freeSample || doc.free_sample) return 'free_sample';
  if (doc.freeDocument || doc.free_document || !(doc.hasPassword || doc.has_password)) return 'free';
  return 'paid';
}

function getFileName(fileUrl?: string, fallback?: string): string | undefined {
  if (!fileUrl) return fallback;
  try {
    const path = fileUrl.includes(SPACES_PREFIX) ? fileUrl.replace(SPACES_PREFIX, '') : fileUrl;
    const base = decodeURIComponent(path.split('?')[0].split('/').pop() || '');
    return base || fallback;
  } catch {
    return fallback;
  }
}

function getFileType(fileUrl?: string): string | undefined {
  const path = fileUrl?.split('?')[0]?.toLowerCase() || '';
  if (path.endsWith('.docx') || path.endsWith('.doc')) return 'word';
  if (path.endsWith('.pptx') || path.endsWith('.ppt')) return 'pptx';
  if (path.endsWith('.pdf')) return 'pdf';
  return undefined;
}

function compact(properties: Record<string, unknown>): Record<string, unknown> {
  return Object.fromEntries(
    Object.entries(properties).filter(([, value]) => value !== undefined && value !== null && value !== ''),
  );
}

/**
 * File size is not stored on documents (fileUrl is a Spaces path string, not a Dato file field).
 */
export function trackDocumentClick(doc: DocumentLike, extras: TrackDocumentClickExtras): void {
  if (!isPosthogEnabled || !doc) return;

  const fileUrl = firstDefined(doc.fileUrl, doc.file_url, extras.destinationUrl);
  const documentName = firstDefined(doc.displayName, doc.display_name, doc.name, doc.title);
  const module = doc.module;

  posthog.capture(
    'document_clicked',
    compact({
      document_id: firstDefined(doc.originalId, doc.original_id, doc.id),
      document_name: documentName,
      file_url: fileUrl,
      file_name: getFileName(fileUrl, documentName),
      file_type: getFileType(fileUrl),
      access: getAccess(doc),
      outcome: extras.outcome,
      source: extras.source,
      is_subscription: Boolean(doc.isSubscription ?? doc.requires_subscription),
      is_free: Boolean(doc.freeDocument ?? doc.free_document),
      is_free_sample: Boolean(doc.freeSample ?? doc.free_sample),
      has_password: Boolean(doc.hasPassword ?? doc.has_password),
      is_subscribed: extras.isSubscribed,
      price: doc.price,
      module: typeof module === 'string' ? module : module?.slug,
      module_title: typeof module === 'object' ? module?.title : undefined,
      categories: asNames(doc.documentCategory || doc.document_category),
      doc_type: firstDefined(doc.docType?.name, doc.doc_type, doc.documentType),
      exam_board: asNames(doc.docExamBoard) || doc.doc_exam_board,
      exam_course: asNames(doc.docExamCourse) || doc.doc_exam_course,
      exam_year: firstDefined(doc.docExamYear, doc.doc_exam_year),
      destination_url: extras.destinationUrl,
    }),
  );
}
